import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useRemoveRentalPriceGroupMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['name'] as const;

export function PriceGroups() {
  const navigate = useNavigate();

  const [deletePriceGroup] = useRemoveRentalPriceGroupMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;
    const name = getColumnData<{label: string}>('name')?.value?.label;

    match(actionKey)
      .with('edit', () => navigate(composePath(settingsRoutes.priceGroupDetail, {params: {id}})))
      .with('delete', () =>
        openDeleteDialog({
          name,
          onConfirm: async () =>
            await deletePriceGroup({rentalPriceGroupId: id})
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
                refreshData();
              })
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('entity.rental.labels.priceGroups'),
        breadcrumbs: [
          {
            label: i18n.t('entity.rental.labels.rental'),
            href: settingsRoutes.priceGroupList,
            isCurrentPage: true,
          },
        ],
        actions: [
          {
            type: 'button',
            title: i18n.t('entity.rental.labels.priceGroupsNew'),
            onClick: () => navigate(settingsRoutes.priceGroupNew),
          },
        ],
      }}
      data-testid={testIds.settings.priceGroupList('template')}
    >
      <DmsDataGrid
        gridCode="rental-price-group"
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.settings.priceGroupList('datagrid')}
      />
    </SettingsTemplate>
  );
}
